<script>
import ContentBox from '@/components/ui/ContentBox';
import DirectoryBrowser from '@/components/file-browser/DirectoryBrowser';
import RequirePlan from '@/components/ui/RequirePlan';
import getErrorMessage from '@/modules/server-errors';
import urlParser from 'url';
import { prettyURL } from '@/modules/prettifier';

export default {
  name: 'SiteForm',
  components: {
    ContentBox,
    DirectoryBrowser,
    RequirePlan
  },
  props: {
    data: Object,
    deleting: Boolean,
    disabled: Boolean,
    invalid: Array,
    isNew: Boolean,
    saving: Boolean
  },
  data() {
    return {
      activeTab: 'general',
      allowedProtocols: ['ftp', 'ftps', 'sftp', 's3', 'webdav'],
      cleanState: {},
      form: {
        name: '',
        url: '',
        classification: 'commercial',
        classificationLocked: false,
        protocol: 'ftp',
        host: '',
        port: '',
        username: '',
        password: '',
        s3AccessKey: '',
        s3SecretKey: '',
        s3Region: '',
        s3Bucket: '',
        s3Acl: false,
        homeFolder: '',
        uploadFolder: '',
        isFavorite: false,
        hasChangeNotification: false,
        isLocked: false,
        canDuplicatePages: true,
        canDeletePages: true,
        gaProfileId: null
      },
      gaProfiles: [],
      hostPlaceholder: this.getDefaultHost('ftp'),
      portPlaceholder: this.getDefaultPort('ftp'),
      showPassword: false,
      showS3SecretKey: false,
      testing: false
    };
  },
  computed: {
    isHttps() {
      return !!this.form.url.match(/^https:\/\//i);
    }
  },
  created() {
    // Set the initial data
    if (this.data) {
      this.setData(this.data);
    }

    // Set the initial clean state
    this.resetCleanState();

    // Fetch Google Analytics profiles
    if (this.$store.state.user.account.gaAuthToken) {
      this.$http
        .get('/services/ga/profiles')
        .then(res => (this.gaProfiles = res.data.profiles))
        .catch(() => (this.gaProfiles = []));
    }
  },
  methods: {
    // Get files for the browse dialog
    getFiles(path) {
      return this.$http
        .get('/utilities/files', {
          params: { path },
          headers: { 'Target-Server': JSON.stringify(this.form) }
        })
        .then(res => res.data.files);
    },

    // Opens the home folder browser
    openHomeFolderBrowser() {
      this.$refs.homeFolderBrowser.open({
        path: this.form.homeFolder
      });
    },

    // Closes the home folder browser
    closeHomeFolderBrowser() {
      this.$refs.homeFolderBrowser.close();
    },

    // Called when a new home folder is selected
    handleHomeFolderSelect(path) {
      this.$refs.homeFolderBrowserPopover.doClose();
      this.form.homeFolder = path;
      this.syncFolders();
    },

    // Opens the upload folder browser
    openUploadFolderBrowser() {
      this.$refs.uploadFolderBrowser.open({
        path: this.form.uploadFolder
      });
    },

    // Closes the upload folder browser
    closeUploadFolderBrowser() {
      this.$refs.uploadFolderBrowser.close();
    },

    // Called when a new upload folder is selected
    handleUploadFolderSelect(path) {
      this.$refs.uploadFolderBrowserPopover.doClose();
      this.form.uploadFolder = path;
      this.syncFolders();
    },

    // Makes sure the upload folder is always underneath the home folder
    syncFolders() {
      // Remove trailing slashes from home folder
      if (this.form.homeFolder.length > 1) {
        this.form.homeFolder = this.form.homeFolder.replace(/\/+$/, '');
      }

      // Remove trailing slashes from upload folder
      if (this.form.uploadFolder.length > 1) {
        this.form.uploadFolder = this.form.uploadFolder.replace(/\/+$/, '');
      }

      const homeFolder = this.form.homeFolder;
      const uploadFolder = this.form.uploadFolder;
      const isBeneath = uploadFolder.substring(0, homeFolder.length) === homeFolder;
      if (!isBeneath) {
        this.form.uploadFolder = homeFolder;
      }
    },

    // Gets the default host for the specified protocol
    getDefaultHost(protocol) {
      switch (protocol) {
        case 'ftp':
        case 'ftps':
          return 'ftp.example.com';
        case 'sftp':
          return 'example.com';
        case 'webdav':
          return 'https://example.com/';
        default:
          return '';
      }
    },

    // Gets the default port for the specified protocol
    getDefaultPort(protocol) {
      switch (protocol) {
        case 'ftp':
        case 'ftps':
          return '21';
        case 'sftp':
          return '22';
        default:
          return '';
      }
    },

    // Returns the form's data
    getData() {
      return Object.assign({}, this.form);
    },

    // Sets the form's data
    setData(data) {
      if (!data.protocol) data.protocol = 'ftp';
      if (data.port === 0) data.port = ''; // allow the placeholder to show

      Object.keys(this.form).forEach(key => {
        this.form[key] = typeof data[key] === 'undefined' ? this.form[key] : data[key];
      });

      this.hostPlaceholder = this.getDefaultHost(this.form.protocol);
      this.portPlaceholder = this.getDefaultPort(this.form.protocol);
    },

    // Tells if the current state is different than the clean state
    isDirty() {
      return JSON.stringify(this.cleanState) !== JSON.stringify(this.form);
    },

    // Converts a URL into a pretty URL
    prettyURL(url) {
      return prettyURL(url);
    },

    // Sets the clean state to the current state
    resetCleanState() {
      this.cleanState = Object.assign({}, this.form);
    },

    // A utility method that lets you test the site's connection
    testConnection() {
      if (this.testing) {
        return;
      }

      this.testing = true;
      return new Promise(resolve => {
        this.$http
          .get('/utilities/files', {
            params: { path: this.form.homeFolder },
            headers: { 'Target-Server': JSON.stringify(this.form) }
          })
          .then(() => {
            this.$notify({
              type: 'success',
              title: this.$t('Test Connection'),
              message: this.$t('Connection successful!')
            });
            resolve(true);
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Test Connection'),
              message: getErrorMessage(err.response.data.code)
            });
            resolve(false);
          })
          .finally(() => (this.testing = false));
      });
    },

    // Called when the host changes
    updateHost() {
      // WebDAV host must be prefixed with a protocol
      if (this.form.protocol === 'webdav') {
        if (!this.form.host.match(/^https?:\/\//)) {
          this.form.host = `http://${this.form.host}`;
        }
      }
    },

    // Called when the protcol changes
    updateProtocol() {
      this.hostPlaceholder = this.getDefaultHost(this.form.protocol);
      this.portPlaceholder = this.getDefaultPort(this.form.protocol);
    },

    // Called when the URL changes
    updateURL() {
      let url = this.form.url;

      // Prepend protocol if missing
      if (!url.match(/^[a-z]+?:\/\//i)) {
        url = `https://${url}`;
      }

      // Parse and reformat the URL
      url = urlParser.format(urlParser.parse(url, false, true));
      if (url === 'http://' || url === 'https://') url = '';

      // Update it
      this.form.url = url;
    }
  }
};
</script>

<template>
  <el-form
    ref="siteForm"
    :disabled="disabled"
    class="site-form"
    label-position="top"
    @submit.native.prevent="$emit('submit')"
  >
    <content-box>
      <el-tabs v-model="activeTab">
        <!-- General -->
        <el-tab-pane :label="$t('General')" name="general">
          <fieldset>
            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
                <!-- Name -->
                <el-form-item :label="$t('Name')" :class="{ 'is-error': invalid.includes('name') }">
                  <el-input ref="name" v-model="form.name" type="text" auto-complete="off" />
                  <div class="text-form-description">
                    {{ $t('Enter a name for your website.') }}
                  </div>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
                <!-- URL -->
                <el-form-item :label="$t('URL')" :class="{ 'is-error': invalid.includes('url') }">
                  <el-input
                    v-model="form.url"
                    type="url"
                    auto-complete="off"
                    placeholder="https://example.com/"
                    @change="updateURL"
                  />
                  <div class="text-form-description">
                    {{ $t("Enter your website's URL.") }}
                  </div>
                </el-form-item>
              </el-col>
            </el-row>

            <!-- SSL warning -->
            <el-alert
              v-if="form.url.match(/^http:\/\//i) && !isHttps"
              :closable="false"
              :title="$t('Warning')"
              :description="
                $t(
                  'If your website doesn\'t use HTTPS, stylesheets and other mixed content will not load in the editor. Please consider securing your website with an SSL certificate.'
                )
              "
              type="warning"
            />

            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="24" :lg="24" :xl="24">
                <!-- Classification -->
                <el-form-item
                  :label="$t('Type of Website')"
                  :class="{ 'is-error': invalid.includes('classification') }"
                >
                  <el-select v-model="form.classification" :disabled="form.classificationLocked">
                    <el-option label="Commercial" value="commercial" />
                    <el-option label="Personal" value="personal" />
                    <el-option label="Educational" value="educational" />
                    <el-option label="Non-profit" value="non-profit" />
                  </el-select>
                  <div class="text-form-description">
                    <i18n
                      path="Please review the {guidelines}. Non-commercial websites are verified to prevent abuse."
                      tag="span"
                    >
                      <template v-slot:guidelines>
                        <a href="https://www.surrealcms.com/docs/website-classifications.html" target="_blank">
                          {{ $t('Website Classification Guidelines') }}
                        </a>
                      </template>
                    </i18n>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>

          <fieldset class="divided">
            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="24" :lg="6" :xl="6">
                <!-- Protocol -->
                <el-form-item :label="$t('Protocol')">
                  <el-select v-model="form.protocol" auto-complete="off" @input="updateProtocol">
                    <el-option label="FTP" value="ftp" />
                    <el-option label="FTP with TLS/SSL" value="ftps" />
                    <el-option label="SFTP" value="sftp" />
                    <el-option label="S3" value="s3" />
                    <el-option label="WebDAV" value="webdav" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col
                v-if="['ftp', 'ftps', 'sftp', 'webdav'].includes(form.protocol)"
                :xs="form.protocol === 'webdav' ? 24 : 16"
                :sm="form.protocol === 'webdav' ? 24 : 16"
                :md="form.protocol === 'webdav' ? 24 : 16"
                :lg="form.protocol === 'webdav' ? 18 : 12"
                :xl="form.protocol === 'webdav' ? 18 : 12"
              >
                <!-- Host -->
                <el-form-item :label="$t('Host')">
                  <el-input
                    v-model="form.host"
                    :placeholder="hostPlaceholder"
                    type="text"
                    auto-complete="off"
                    @change="updateHost"
                  />
                </el-form-item>
              </el-col>

              <el-col v-if="['ftp', 'ftps', 'sftp'].includes(form.protocol)" :xs="8" :sm="8" :md="8" :lg="6" :xl="6">
                <!-- Port -->
                <el-form-item :label="$t('Port')">
                  <el-input v-model="form.port" :placeholder="portPlaceholder" type="number" auto-complete="off" />
                </el-form-item>
              </el-col>

              <el-col v-if="['s3'].includes(form.protocol)" :xs="24" :sm="24" :md="24" :lg="9" :xl="9">
                <!-- Region -->
                <el-form-item :label="$t('Region')">
                  <el-input v-model="form.s3Region" type="text" auto-complete="off" placeholder="us-east-1" />
                </el-form-item>
              </el-col>

              <el-col v-if="['s3'].includes(form.protocol)" :xs="24" :sm="24" :md="24" :lg="9" :xl="9">
                <!-- Bucket -->
                <el-form-item :label="$t('Bucket')">
                  <el-input v-model="form.s3Bucket" type="text" auto-complete="off" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col
                v-if="['ftp', 'ftps', 'sftp', 'webdav'].includes(form.protocol)"
                :xs="24"
                :sm="24"
                :md="12"
                :lg="12"
                :xl="12"
              >
                <!-- Username -->
                <el-form-item :label="$t('Username')">
                  <el-input v-model="form.username" type="text" auto-complete="off" />
                </el-form-item>
              </el-col>

              <el-col
                v-if="['ftp', 'ftps', 'sftp', 'webdav'].includes(form.protocol)"
                :xs="24"
                :sm="24"
                :md="12"
                :lg="12"
                :xl="12"
              >
                <!-- Password -->
                <el-form-item :label="$t('Password')">
                  <el-input
                    ref="password"
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    auto-complete="off"
                  >
                    <a slot="suffix" href="#" @click.prevent="showPassword = !showPassword">
                      <i :class="showPassword ? 'el-icon-fa-eye-slash' : 'el-icon-fa-eye'" />
                    </a>
                  </el-input>
                </el-form-item>
              </el-col>

              <el-col v-if="['s3'].includes(form.protocol)" :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                <!-- Access Key -->
                <el-form-item :label="$t('Access Key')">
                  <el-input v-model="form.s3AccessKey" type="text" auto-complete="off" />
                </el-form-item>
              </el-col>

              <el-col v-if="['s3'].includes(form.protocol)" :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                <!-- Secret Key -->
                <el-form-item :label="$t('Secret Access Key')">
                  <el-input
                    ref="s3SecretKey"
                    v-model="form.s3SecretKey"
                    :type="showS3SecretKey ? 'text' : 'password'"
                    auto-complete="off"
                  >
                    <a slot="suffix" href="#" @click.prevent="showS3SecretKey = !showS3SecretKey">
                      <i :class="showS3SecretKey ? 'el-icon-fa-eye-slash' : 'el-icon-fa-eye'" />
                    </a>
                  </el-input>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col v-if="['s3'].includes(form.protocol)" :xs="24">
                <!-- ACL -->
                <el-form-item>
                  <el-switch
                    v-model="form.s3Acl"
                    :checked="form.s3Acl"
                    :active-text="$t('Access Control List (ACL)')"
                    auto-complete="off"
                  />
                  <div class="text-form-description">
                    {{
                      $t(
                        'If your bucket has ACLs enabled, enable this option to give "public-read" permissions to uploaded files.'
                      )
                    }}
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>

          <fieldset class="divided">
            <!-- Home folder -->
            <el-form-item :label="$t('Home Folder')">
              <el-input
                v-model="form.homeFolder"
                type="text"
                auto-complete="off"
                prefix-icon="el-icon-fa-folder"
                @change="syncFolders"
              >
                <template slot="append">
                  <el-popover
                    ref="homeFolderBrowserPopover"
                    placement="bottom"
                    trigger="click"
                    @show="openHomeFolderBrowser()"
                    @hide="closeHomeFolderBrowser()"
                  >
                    <el-button slot="reference">
                      {{ $t('Browse') }}
                    </el-button>

                    <directory-browser
                      ref="homeFolderBrowser"
                      :fetch-method="getFiles"
                      :multiple="false"
                      base-path=""
                      type="popover"
                      @select="handleHomeFolderSelect"
                      @close="$refs.homeFolderBrowserPopover.doClose()"
                    />
                  </el-popover>
                </template>
              </el-input>
              <div class="text-form-description">
                {{ $t('Select the folder that contains your homepage.') }}
              </div>
            </el-form-item>

            <!-- Upload folder -->
            <el-form-item :label="$t('Upload Folder')">
              <el-input
                v-model="form.uploadFolder"
                type="text"
                auto-complete="off"
                prefix-icon="el-icon-fa-folder"
                @change="syncFolders"
              >
                <template slot="append">
                  <el-popover
                    ref="uploadFolderBrowserPopover"
                    placement="bottom"
                    trigger="click"
                    @show="openUploadFolderBrowser()"
                    @hide="closeUploadFolderBrowser()"
                  >
                    <el-button slot="reference">
                      {{ $t('Browse') }}
                    </el-button>

                    <directory-browser
                      ref="uploadFolderBrowser"
                      :base-path="form.homeFolder"
                      :fetch-method="getFiles"
                      :multiple="false"
                      type="popover"
                      @select="handleUploadFolderSelect"
                      @close="$refs.uploadFolderBrowserPopover.doClose()"
                    />
                  </el-popover>
                </template>
              </el-input>
              <div class="text-form-description">
                {{ $t('Select the folder where uploaded files will be stored.') }}
              </div>
            </el-form-item>
          </fieldset>
        </el-tab-pane>

        <!-- Services -->
        <el-tab-pane :label="$t('Services')" name="services">
          <fieldset>
            <!-- GA Profile -->
            <el-form-item :label="$t('Analytics')">
              <el-select v-model="form.gaProfileId" :disabled="!$store.state.user.account.gaAuthToken" placeholder="">
                <el-option :label="$t('None')" :value="null" />
                <el-option
                  v-for="profile in gaProfiles"
                  :key="profile.id"
                  :label="`${prettyURL(profile.websiteUrl)} (${profile.name})`"
                  :value="profile.id"
                >
                  <span style="float: left">
                    {{ `${prettyURL(profile.websiteUrl)} (${profile.name})` }}
                  </span>
                  <small class="text-muted" style="float: right">
                    {{ profile.webPropertyId }}
                  </small>
                </el-option>
              </el-select>
              <div class="text-form-description">
                <span v-if="$store.state.user.account.gaAuthToken">
                  {{ $t('Select a profile to enable the analytics dashboard for this site.') }}
                </span>
                <span v-else>
                  {{ $t('Go to the settings page to enable this feature.') }}
                </span>
              </div>
            </el-form-item>
          </fieldset>
        </el-tab-pane>

        <!-- Advanced -->
        <el-tab-pane :label="$t('Advanced')" name="advanced">
          <fieldset>
            <!-- Favorite -->
            <el-form-item>
              <el-switch
                v-model="form.isFavorite"
                :checked="form.isFavorite"
                :active-text="$t('Add To Favorites')"
                auto-complete="off"
              />
              <div class="text-form-description">
                {{ $t('Your favorites will appear in the sidebar for quick access.') }}
              </div>
            </el-form-item>

            <!-- Lockdown -->
            <el-form-item>
              <el-switch
                v-model="form.isLocked"
                :checked="form.isLocked"
                :active-text="$t('Enable Lockdown')"
                auto-complete="off"
              />
              <div class="text-form-description">
                {{ $t("When enabled, users won't be able to publish changes to the site.") }}
              </div>
            </el-form-item>

            <!-- Change notifications -->
            <el-form-item>
              <el-switch
                v-model="form.hasChangeNotification"
                :checked="form.hasChangeNotification"
                :disabled="$store.state.user.account.plan !== 'agency'"
                :active-text="$t('Enable Change Notifications')"
                auto-complete="off"
              />
              <require-plan plan="agency" />
              <div class="text-form-description">
                {{ $t('Receive an email when others publish changes to this site.') }}
              </div>
            </el-form-item>
          </fieldset>

          <fieldset class="divided">
            <!-- Duplicate Pages -->
            <el-form-item>
              <el-switch
                v-model="form.canDuplicatePages"
                :checked="form.canDuplicatePages"
                :active-text="$t('Duplicate Pages')"
                auto-complete="off"
              />
              <div class="text-form-description">
                {{ $t('Allow users to duplicate pages on this site.') }}
              </div>
            </el-form-item>

            <!-- Delete pages -->
            <el-form-item>
              <el-switch
                v-model="form.canDeletePages"
                :checked="form.canDeletePages"
                :active-text="$t('Delete Pages')"
                auto-complete="off"
              />
              <div class="text-form-description">
                {{ $t('Allow users to delete pages on this site.') }}
              </div>
            </el-form-item>
          </fieldset>

          <!-- Delete -->
          <fieldset v-if="!isNew" class="divided">
            <h2>{{ $t('Delete') }}</h2>
            <p>{{ $t('This will delete the site from the CMS, but not from your web server.') }}</p>
            <el-form-item>
              <el-button :loading="deleting" type="danger" @click="$emit('delete')">
                {{ $t('Delete This Site') }}
              </el-button>
            </el-form-item>
          </fieldset>
        </el-tab-pane>
      </el-tabs>
    </content-box>

    <div class="text-right">
      <!-- Cancel -->
      <el-button @click="$emit('cancel')">
        {{ $t('Cancel') }}
      </el-button>

      <!-- Submit -->
      <el-button :loading="saving" type="primary" native-type="submit">
        <span v-if="isNew">{{ $t('Create Site') }}</span>
        <span v-else>{{ $t('Save Changes') }}</span>
      </el-button>
    </div>
  </el-form>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

//
// No styles yet
//
</style>
